import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TOKEN_REQUEST } from '@auth/msalConfig';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { executeQuery } from '@components/executeQuery';
import Feedback from './Feedback';  // Import the new Feedback component

const renderers = {
  link: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#007BFF', fontWeight: 'bold' }}>
      {children} <small>(source)</small>
    </a>
  ),
};

const useMsalAuth = () => {
  const { instance, accounts } = useMsal();
  const userEmail = accounts[0]?.username;
  const [token, setToken] = useState(null);

  const refreshToken = async () => {
    try {
      const tokenResponse = await instance.acquireTokenSilent(TOKEN_REQUEST);
      setToken(tokenResponse.accessToken);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(TOKEN_REQUEST);
      } else {
        console.error('Token refresh failed:', error);
      }
    }
  };

  const logout = async () => {
    try {
      await instance.logoutPopup();
      setToken(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    refreshToken();
  }, []);

  return { userEmail, token, refreshToken, logout };
};

const Chat = () => {
  const { userEmail, logout } = useMsalAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typingMessage, setTypingMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const sendMessage = async () => {
    if (!prompt || loading) return;

    setLoading(true);
    setTypingMessage('');
    
    // Insert the user's message and get its ID
    const userMessage = await executeQuery(`
      INSERT INTO conversation_website (user_id, message, role) VALUES (?, ?, ?);
    `, [userEmail, prompt, 'user']);
    setMessages((prevMessages) => [...prevMessages, { id: userMessage.insertId, role: 'user', content: prompt }]);
    setPrompt('');

    // Send prompt to backend API
    try {
      // Developemnt https://localhost:3000
      // Production https://rolo-backend-506573180721.europe-west1.run.app/
      const response = await fetch(' https://rolo-backend-506573180721.europe-west1.run.app/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, user_id: userEmail }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        assistantMessage += decoder.decode(value);

        // Timeout to slow the typing effect 
        await new Promise((resolve) => setTimeout(resolve, 50));
        setTypingMessage((prev) => prev + decoder.decode(value));
      }

      // Save assistant's response with ID for feedback
      const assistantEntry = await executeQuery(`
        INSERT INTO conversation_website (user_id, message, role) VALUES (?, ?, ?);
      `, [userEmail, assistantMessage, 'assistant']);
      setMessages((prevMessages) => [...prevMessages, { id: assistantEntry.insertId, role: 'assistant', content: assistantMessage }]);
      setTypingMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between">
        <button className="btn btn-outline-danger m-3" onClick={logout}>
          <i className="bi bi-box-arrow-right"></i> Logout
        </button>
  
        {/* Dashboard and Exam Mode Buttons */}
        <div className="d-flex">
          <button
            className="btn btn-outline-primary m-3"
            onClick={() => navigate("/dashboard")}
          >
            <i className="bi bi-bar-chart"></i> Dashboard
          </button>
          {/* <button
            className="btn btn-outline-primary m-3"
            onClick={() => navigate("/exam_mode")}
          >
            <i className="bi bi-file-earmark-check"></i> Exam Mode
          </button> */}
        </div>
      </div>
  
      <div className="d-flex align-items-center justify-content-center mb-4">
        <img src="/favicon-96x96.png" alt="Icon" style={{ width: '50px', marginRight: '20px' }} />
        <h1>Rolo Tutor</h1>
      </div>

      <div className="mb-3">
  {messages.map((message, index) => (
    <div key={index} className="d-flex flex-column mb-4">
      <div
        className={`d-flex ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
      >
        <div
          className={`p-3 rounded shadow ${message.role === 'user' ? 'bg-secondary text-white' : 'bg-light text-dark'}`}
          style={{
            maxWidth: '70%',
            borderRadius: '25px',
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={renderers}>{message.content}</ReactMarkdown>
        </div>
      </div>

      {/* Feedback Buttons Below the Bubble */}
      {message.role === 'assistant' && (
        <div
          className={`d-flex ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
          style={{ marginTop: '5px' }}
        >
          <Feedback
            messageId={message.id}
            userEmail={userEmail}
            messageContent={message.content}
          />
        </div>
      )}
    </div>
  ))}

  {typingMessage && (
    <div className="d-flex mb-4 justify-content-start">
      <div
        className="p-3 rounded shadow bg-light text-dark"
        style={{
          maxWidth: '70%',
          borderRadius: '25px',
        }}
      >
        <strong>Assistant:</strong> {typingMessage}
      </div>
    </div>
  )}

  {loading && !isTyping && (
    <div className="text-center mb-3">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p>Thinking...</p>
    </div>
  )}

  <div className="input-group mb-3">
    <input
      type="text"
      className="form-control"
      value={prompt}
      onChange={(e) => setPrompt(e.target.value)}
      disabled={loading || isTyping}
      placeholder="Type your message..."
      onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
    />
    <button className="btn btn-outline-primary" onClick={sendMessage} disabled={loading}>
      Send
    </button>
  </div>
</div>

    </div>
  );
};

export default Chat;